import { VERIFICATION_TYPES } from 'helper/constant';

// ─── Node & Layout Sizes ────────────────────────────────────────────────────
export const NODE_SIZE = {
    IDENTITY_METHOD: { width: 220, height: 80 },
    CHECK: { width: 210, height: 72 },
    DECISION: { width: 260, height: 48 },
    TERMINAL: { width: 140, height: 40 },
    HEADER: { width: 280, height: 64 },
};

// ─── Ordered list of identity methods (for consistent left-to-right layout) ──
export const IDENTITY_METHOD_ORDER = [
    VERIFICATION_TYPES.GOVT_ID,
    VERIFICATION_TYPES.KBA,
    VERIFICATION_TYPES.BANK,
];

// ─── Layout Gaps ──────────────────────────────────────────────────────────────
export const GAP = {
    IDENTITY_H: 100, // horizontal gap between identity method nodes (space for AND/OR label)
    SELFIE_V: 90, // vertical gap between methods row and selfie (space for AND label)
    SECTION_V: 60, // vertical gap between major flowchart sections
    BRANCH_H: 80, // horizontal gap between left/right check branches
    CHECK_V: 40, // vertical gap within a check branch (check → decision)
    TERM_H_OFFSET: 60, // horizontal gap from decision right edge to terminal
    TERM_V_OFFSET: 20, // vertical gap from decision bottom to No-terminal top
    LIVENESS_V: 30, // vertical gap between liveness decision rows
    GROUP_PADDING: 32, // inner padding of the identity group node
    LIVENESS_DEC_OFFSET: 60, // horizontal gap from selfie right edge to liveness decisions
    FACE_MATCH_TERM_X_ADJUST: -40, // X correction: face match decision renders narrower than DW
    LIVENESS_TERM_X_ADJUST: 40, // X correction: liveness decision text renders wider than DW
    LIVENESS_TERM_Y_INSET: 20, // pulls Yes/No terminals toward decision center (tighter T-fork)
};

// ─── Additional Check Type Keys ───────────────────────────────────────────────
export const CHECK_TYPES = {
    AUTHORITATIVE_DB: 'authoritative_db',
    SEVEN_YEAR: 'seven_year_criminal_report',
    GLOBAL_WATCHLIST: 'global_watchlist',
};

// ─── Node ID registry ─────────────────────────────────────────────────────────
// All node IDs are centralised here to avoid hardcoded strings in builder functions.
export const NODE_IDS = {
    HEADER: 'node_header',
    GROUP_IDENTITY: 'group_identity',
    DECISION_IDENTITY_PROOF: 'decision_identity_proof',
    TERMINAL_IDENTITY_PROOF_ERROR: 'terminal_identity_proof_error',
    TERMINAL_IDENTITY_PROOF_CLEAR: 'terminal_identity_proof_clear',
    // Identity method nodes
    GOVT_ID: 'node_govt_id',
    KBA: 'node_kba',
    BANK: 'node_bank',
    SELFIE: 'node_selfie',
    // Liveness decision + terminal nodes (inside identity group)
    DECISION_FACE_MATCH: 'decision_face_match',
    DECISION_LIVENESS: 'decision_liveness',
    TERMINAL_FM_CLEAR: 'terminal_face_match_clear',
    TERMINAL_FM_REVIEW: 'terminal_face_match_review',
    TERMINAL_LIV_CLEAR: 'terminal_liveness_clear',
    TERMINAL_LIV_REVIEW: 'terminal_liveness_review',
    // Additional check nodes
    CHECK_GLOBAL_WATCHLIST: 'check_global_watchlist',
    DECISION_GLOBAL_WATCHLIST: 'decision_global_watchlist',
    TERMINAL_GW_REVIEW: 'terminal_global_watchlist_review',
    TERMINAL_GW_CLEAR: 'terminal_global_watchlist_clear',
    CHECK_AUTH_DB: 'check_authoritative_db',
    DECISION_AUTH_DB: 'decision_authoritative_db',
    TERMINAL_AUTH_DB_ERROR: 'terminal_authoritative_db_error',
    TERMINAL_AUTH_DB_CLEAR: 'terminal_authoritative_db_clear',
    CHECK_SEVEN_YEAR: 'check_seven_year_criminal_report',
    DECISION_SEVEN_YEAR: 'decision_seven_year_criminal_report',
    TERMINAL_SYR_REVIEW: 'terminal_seven_year_criminal_report_review',
    TERMINAL_SYR_CLEAR: 'terminal_seven_year_criminal_report_clear',
};

// ─── Labels ───────────────────────────────────────────────────────────────────
// Decision node labels are also used in YesNoEdge to determine color inversion.
export const LABELS = {
    // Identity method display names (keyed by type string for easy lookup)
    govt_id: 'Government ID',
    kba: 'KBA',
    bank: 'Financial Account Ownership',
    selfie: 'Selfie',

    // Additional check display names (keyed by type string for easy lookup)
    authoritative_db: 'Authoritative Database',
    seven_year_criminal_report: '7 Year Criminal Activity Check',
    global_watchlist: 'Global Watchlist Check',

    // Decision node labels
    IDENTITY_PROOF_VALID: 'IDENTITY PROOF VALID',
    AUTHORITATIVE_DB_CHECK_PASSED: 'AUTHORITATIVE DB CHECK PASSED',
    FACE_MATCH_PASSED: 'FACE MATCH PASSED',
    LIVENESS_CONFIDENCE_SCORE_HIGH: 'LIVENESS CONFIDENCE SCORE HIGH',
    GLOBAL_WATCHLIST_RECORD_FOUND: 'GLOBAL WATCHLIST RECORD FOUND',
    SEVEN_YEAR_CRIMINAL_RECORD_FOUND: 'SEVEN YEAR CRIMINAL RECORD FOUND',

    // Terminal node labels
    CLEAR: 'Clear',
    NEEDS_REVIEW: 'Needs Review',
    END_CHECK_ERROR: 'End - Check Error',
};

// Colors
export const COLORS = {
    BLUE: '#1570EF',
    GREEN: '#039855',
    ORANGE: '#DC6803',
    EDGE_GRAY: '#98A2B3',
};

// ─── Marker end style for edge arrows ────────────────────────────────────────
export const MARKER_END = {
    type: 'arrowclosed',
    width: 16,
    height: 16,
    color: '#98A2B3', // COLORS.EDGE_GRAY
};

// ─── Additional checks layout fine-tuning ────────────────────────────────────
// All position adjustments for buildAdditionalChecks live here.
// Edit these values to reposition nodes without touching the builder logic.
export const ADDITIONAL_CHECKS = {
    // ── Shared ──────────────────────────────────────────────────────────────
    BRANCH_X_SPREAD: 70, // extra horizontal spread (beyond BRANCH_H) for both check branches
    CHECK_DECISION_V_EXTRA: 30, // extra vertical gap between check node and its decision node
    CLEAR_TERM_V_EXTRA: 30, // extra vertical gap below decision for Clear terminal

    // ── Global Watchlist (left branch) ──────────────────────────────────────
    GW_DECISION_X_ADJ: 36, // fine-tune decision x centering under check
    GW_REVIEW_X_EXTRA: 40, // extra left offset for Needs Review terminal
    GW_CLEAR_X_ADJ: 48, // fine-tune Clear terminal x centering

    // ── Authoritative Database (right branch) ────────────────────────────────
    AUTH_DECISION_X_ADJ: 39.5, // fine-tune decision x centering under check
    AUTH_ERROR_X_EXTRA: 50, // extra right offset for End Check Error terminal
    AUTH_CLEAR_X_ADJ: 47, // fine-tune Clear terminal x centering (no seven year case)

    // ── 7 Year Criminal (sequential below auth_db) ───────────────────────────
    SYR_CHECK_V_REDUCE: 20, // reduce vertical gap before seven_year check node
    SYR_DECISION_X_ADJ: -14, // fine-tune decision x centering under check
    SYR_DECISION_V_EXTRA: 20, // extra vertical gap between check and decision
    SYR_REVIEW_X_EXTRA: 70, // extra right offset for Needs Review terminal
    SYR_CLEAR_X_ADJ: 43, // fine-tune Clear terminal x centering

    // ── Identity Proof node positioning ──────────────────────────────────────
    IDENTITY_PROOF_X_ADJ: 34.5, // shifts identity proof decision right to center under canvas
    IDENTITY_PROOF_ERROR_X_EXTRA: 20, // extra right offset for identity proof error terminal

    // ── No additional checks fallback (Identity Proof → Clear) ───────────────
    SECTION_Y_OFFSET: 40, // Y offset passed into buildAdditionalChecks from IDENTITY_PROOF_Y
    IP_CLEAR_X_ADJ: 39, // fine-tune Clear terminal x centering under identity proof
    IP_CLEAR_Y_EXTRA: 20, // extra vertical gap below decision for Clear terminal

    // ── Single check layout (only GW or only AuthDB present) ─────────────────
    SINGLE_CHECK_X_ADJ: -35, // X shift to center the single check under identity proof decision
};
